import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useEffectEvent,
} from 'react';
import { io } from 'socket.io-client';
import Snackbar from '@mui/material/Snackbar';
import Slide from '@mui/material/Slide';
import Alert from '@mui/material/Alert';

import { ClinicRxContext } from '../App';
import { getSocketURL } from '../utils';

const SOCKET_URL = getSocketURL();
const SOCKET_MESSAGES = {
  CONNECTING: 'Connecting to real-time server...',
  WAITING: 'Waiting to connect to the real-time server...',
  ERROR:
    'An error occurred while connecting to the real-time server. Please try again.',
};

const CONNECT = 'connect';
const CONNECT_ERROR = 'connect_error';
const ALREADY_LOGGED_IN_USERS = 'already-logged-in-users';
const USER_LOGOUT = 'user-logout';
const USER_LOGIN = 'user-login';

export const SocketContext = createContext(null);

export default function SocketProvider({ children }) {
  const { state } = useContext(ClinicRxContext);
  const {
    name: { first, last },
    chatID,
    userType,
    organization,
  } = state.user;
  const socketUserName = `${first} ${last}`;

  const [isSnackbarOpen, setIsSnackbarOpen] = useState(true);
  const [snackbarMessage, setSnackbarMessage] = useState(
    SOCKET_MESSAGES.CONNECTING,
  );
  const [loggedInUsers, setLoggedInUsers] = useState([]);

  const socketRef = useRef(null);

  const onConnect = () => {
    setIsSnackbarOpen(false);
    setSnackbarMessage('');
  };

  const onConnectError = () => {
    setSnackbarMessage(SOCKET_MESSAGES.ERROR);
  };

  const onAlreadyLoggedInusers = (users) => {
    console.log('Received already logged in users:', users);
    const { alreadyLoggedInUsers } = JSON.parse(users);
    setLoggedInUsers(alreadyLoggedInUsers);
  };

  const onUserLogout = (user) => {
    console.log('Received user log out event:', user);
    const { userID } = JSON.parse(user);
    setLoggedInUsers((prev) =>
      prev.filter((loggedIn) => loggedIn.userID !== userID),
    );
  };

  const handleUserLogin = (user) => {
    console.log('Received user log in event:', user);
    const { userID, userName, organization } = JSON.parse(user);
    setLoggedInUsers((prev) => [...prev, { userID, userName, organization }]);
    if (!isSnackbarOpen) {
      setIsSnackbarOpen(true);
      setSnackbarMessage(`${userName} connected.`);
    }
  };

  const onUserLogin = useEffectEvent(handleUserLogin);

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      auth: {
        userID: chatID,
        name: socketUserName,
        organization,
        userType,
      },
    });
    // This is what triggers
    // the re-render when
    // connected.
    socket.on(CONNECT, onConnect);
    socket.on(CONNECT_ERROR, onConnectError);
    socket.on(ALREADY_LOGGED_IN_USERS, onAlreadyLoggedInusers);
    socket.on(USER_LOGOUT, onUserLogout);
    socket.on(USER_LOGIN, onUserLogin);

    socketRef.current = socket;
    return () => {
      socket.off(CONNECT, onConnect);
      socket.off(CONNECT_ERROR, onConnectError);
      socket.off(ALREADY_LOGGED_IN_USERS, onAlreadyLoggedInusers);
      socket.off(USER_LOGOUT, onUserLogout);
      socket.off(USER_LOGIN, onUserLogin);

      socketRef.current = null;
      socket.disconnect();
    };
  }, []);

  return (
    <>
      <Snackbar
        open={isSnackbarOpen}
        onClose={() => {
          setIsSnackbarOpen(false);
          setSnackbarMessage('');
        }}
        slots={{
          transition: Slide,
        }}
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        key='Slide'
        autoHideDuration={6900}
      >
        <Alert severity='info'>{snackbarMessage}</Alert>
      </Snackbar>
      <SocketContext
        value={{
          socket: socketRef.current,
          connectionID: chatID,
          connectionName: socketUserName,
          snackbar: {
            setIsOpen: setIsSnackbarOpen,
            setMessage: setSnackbarMessage,
            isOpen: isSnackbarOpen,
          },
          loggedInUsers,
        }}
      >
        {socketRef.current?.connected ? (
          children
        ) : (
          <p>{SOCKET_MESSAGES.WAITING}</p>
        )}
      </SocketContext>
    </>
  );
}
